import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Lock, Video, CheckCircle2, ArrowRight, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { MEMBER_NAV } from '../../constants/nav';
import { fetchOwnedLesson, completeLesson, fetchPublicCourseById, formatPrice } from '../../features/courses/api/courses';
import type { Lesson, Course } from '../../types';
import { ShareLesson } from '../../components/courses/ShareLesson';
import { COURSE } from '../../constants/testIds';
import { toEmbedUrl } from '../../utils/video';
import { PdfReader } from '../../components/courses/PdfReader';

export default function LessonDetailPage() {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [locked, setLocked] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [nextId, setNextId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!courseId || !lessonId) return;
    setLoading(true);
    setDenied(false);
    setLocked(false);
    fetchOwnedLesson(courseId, lessonId)
      .then((l) => {
        setLesson(l);
        setCompleted(!!l.completed);
        setNextId(l.next_lesson_id ?? null);
      })
      .catch((e) => {
        const msg = e.response?.data?.detail || '';
        if (e.response?.status === 403 && /sebelumnya|previous/i.test(msg)) {
          setLocked(true);
        } else if (e.response?.status === 403) {
          setDenied(true);
          fetchPublicCourseById(courseId).then(setCourse).catch(() => setCourse(null));
        }
      })
      .finally(() => setLoading(false));
  }, [courseId, lessonId]);

  const markCompleted = async () => {
    if (!courseId || !lessonId) return;
    setSaving(true);
    try {
      const res = await completeLesson(courseId, lessonId);
      setCompleted(true);
      setNextId(res.next_lesson_id);
      toast.success('Lesson selesai!', {
        description: res.next_lesson_id ? 'Lesson berikutnya kini terbuka.' : 'Anda telah menyelesaikan semua lesson.',
      });
    } catch (e: any) {
      toast.error('Gagal menyimpan', { description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const goNext = () => {
    if (nextId) navigate(`/member/courses/${courseId}/lessons/${nextId}`);
  };

  return (
    <DashboardLayout navItems={MEMBER_NAV} title="Lesson">
      <div className="max-w-4xl">
        <Link to={`/member/courses/${courseId}`} className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-400 mb-6 text-sm transition-colors">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Kembali ke course
        </Link>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} />
          </div>
        ) : locked ? (
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-10 text-center" data-testid="lesson-locked">
            <Lock className="w-10 h-10 text-amber-400/70 mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-white/70 font-medium">Lesson ini masih terkunci</p>
            <p className="text-white/50 text-sm mt-1">Selesaikan lesson sebelumnya terlebih dahulu untuk membuka materi ini.</p>
            <button
              onClick={() => navigate(`/member/courses/${courseId}`)}
              className="mt-5 bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300"
            >
              Kembali ke daftar lesson
            </button>
          </div>
        ) : denied ? (
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-10 text-center" data-testid="lesson-denied">
            <Lock className="w-10 h-10 text-white/30 mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-white/70 font-medium">Anda belum memiliki akses ke materi ini</p>
            {course ? (
              <>
                <p className="text-white/50 text-sm mt-1">
                  Materi ini bagian dari course <span className="text-white">{course.title}</span>
                  {course.price ? ` · ${formatPrice(course.price, course.currency)}` : ' · Gratis untuk member'}
                </p>
                <Link
                  data-testid="lesson-denied-cta"
                  to={`/courses/${course.slug}`}
                  className="inline-block mt-5 bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300"
                >
                  {course.price ? 'Beli Course Ini' : 'Buka Course Ini'}
                </Link>
              </>
            ) : (
              <p className="text-white/50 text-sm mt-1">Hubungi admin untuk mendapatkan akses materi ini.</p>
            )}
          </div>
        ) : lesson ? (
          <div data-testid={COURSE.lessonDetail}>
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">{lesson.title}</h2>
              {completed && (
                <span className="text-[11px] uppercase tracking-wider text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2.5 py-1 rounded-full shrink-0">
                  Selesai
                </span>
              )}
            </div>
            <p className="text-white/60 mb-6">{lesson.description}</p>

            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <p className="text-xs text-white/40">Bagikan materi ini:</p>
              <ShareLesson courseId={courseId!} lessonId={lesson.id} title={lesson.title} />
            </div>

            {lesson.content_type === 'pdf' ? (
              <div className="mb-6" data-testid="lesson-pdf">
                {lesson.pdf_url ? (
                  <>
                    <PdfReader
                      url={lesson.pdf_url}
                      name={lesson.pdf_name}
                      allowDownload={lesson.allow_download}
                      storageKey={`lesson_pdf_page_${lesson.id}`}
                      lessonId={lesson.id}
                    />
                    <p className="text-[11px] text-white/35 mt-2">
                      {lesson.pdf_name || 'Materi PDF'}
                      {lesson.allow_download ? ' · boleh diunduh' : ' · hanya dapat dibaca di aplikasi'}
                      {' · posisi halaman terakhir Anda disimpan otomatis'}
                    </p>
                  </>
                ) : (
                  <div className="rounded-2xl border border-white/10 bg-black/40 py-20 text-center text-white/40">
                    <FileText className="w-12 h-12 mx-auto mb-3" strokeWidth={1.5} />
                    <p>Materi PDF akan segera tersedia</p>
                  </div>
                )}
              </div>
            ) : (
              <div
                data-testid={COURSE.lessonVideo}
                className="aspect-video w-full rounded-2xl border border-white/10 bg-black/40 overflow-hidden flex items-center justify-center mb-6"
              >
                {lesson.video_url ? (
                  <iframe
                    src={toEmbedUrl(lesson.video_url)}
                    title={lesson.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div className="text-center text-white/40">
                    <Video className="w-12 h-12 mx-auto mb-3" strokeWidth={1.5} />
                    <p>Video akan segera tersedia</p>
                  </div>
                )}
              </div>
            )}

            {/* Progress actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <button
                data-testid={COURSE.lessonComplete}
                onClick={markCompleted}
                disabled={saving || completed}
                className={`flex items-center justify-center gap-2 font-medium px-6 py-3 rounded-lg transition-colors ${
                  completed
                    ? 'bg-emerald-400/15 text-emerald-300 border border-emerald-400/30 cursor-default'
                    : 'bg-cyan-400 text-[#050914] hover:bg-cyan-300 disabled:opacity-60'
                }`}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                ) : (
                  <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                )}
                {completed ? 'Lesson Selesai' : 'Lesson Completed'}
              </button>

              <button
                data-testid={COURSE.lessonNext}
                onClick={goNext}
                disabled={!completed || !nextId}
                title={!completed ? 'Selesaikan lesson ini dulu' : !nextId ? 'Ini lesson terakhir' : ''}
                className="flex items-center justify-center gap-2 bg-white/5 text-white border border-white/10 font-medium px-6 py-3 rounded-lg transition-colors hover:bg-white/10 hover:text-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-white disabled:hover:bg-white/5"
              >
                Next Lesson <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
            {!completed && (
              <p className="text-xs text-white/40 mt-3">
                Klik <span className="text-cyan-400">"Lesson Completed"</span> untuk membuka lesson berikutnya.
              </p>
            )}
            {completed && !nextId && (
              <p className="text-xs text-emerald-400/70 mt-3">Selamat! Anda telah menyelesaikan semua lesson di course ini.</p>
            )}
          </div>
        ) : (
          <div className="text-white/50 py-20 text-center">Lesson tidak ditemukan.</div>
        )}
      </div>
    </DashboardLayout>
  );
}
